/*
 * Home page behaviour.
 *
 * <hero-slider> turns the hero's slides (sections/hero.liquid) into a slider.
 * The slides already sit side by side in a scroll-snapping track, so touch
 * screens swipe natively and the page works without this script; it adds the
 * previous/next buttons (looping round at either end), one dot per slide, and
 * optional autoplay ([data-autoplay] = milliseconds per slide). Slides out of
 * view are inert, so keyboard focus stays on the one shown. The slides after
 * the first have the browser's own lazy images. Autoplay never runs
 * for shoppers who prefer reduced motion, waits while the slider is hovered,
 * focused, scrolled away, or in a background tab, and stops for good with the
 * pause button. In the theme editor, selecting a slide's block shows it.
 *
 * <hero-video> plays a slide's muted background video while that slide is in
 * view (the track clips the others) and pauses it when scrolled away. It never
 * starts for shoppers who prefer reduced motion or have data saving on; the
 * image underneath stays as the poster. The video fades in once it's actually
 * playing.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class HeroSlider extends HTMLElement {
  #abort;
  #observer;
  #frame;
  #timer;
  #index = 0;
  #holds = new Set();

  connectedCallback() {
    this.track = this.querySelector('[data-hero-track]');
    this.slides = [...this.querySelectorAll('[data-hero-slide]')];
    if (!this.track || this.slides.length < 2) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.dots = [...this.querySelectorAll('[data-hero-dot]')];
    this.pauseButton = this.querySelector('[data-hero-pause]');

    for (const button of this.querySelectorAll('[data-hero-step]')) {
      button.addEventListener('click', () => this.go(this.#index + Number(button.dataset.heroStep)), { signal });
    }
    for (const dot of this.dots) {
      dot.addEventListener('click', () => this.go(Number(dot.dataset.heroDot)), { signal });
    }
    this.track.addEventListener('scroll', this.#onScroll, { signal, passive: true });

    // Theme editor: show the slide whose block is selected, and hold autoplay meanwhile.
    document.addEventListener(
      'shopify:block:select',
      (event) => {
        const index = this.slides.indexOf(event.target);
        if (index < 0) return;
        this.#hold('editor', true);
        this.go(index, true);
      },
      { signal }
    );
    document.addEventListener(
      'shopify:block:deselect',
      (event) => {
        if (this.slides.includes(event.target)) this.#hold('editor', false);
      },
      { signal }
    );

    this.#update();
    this.#setUpAutoplay(signal);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    clearTimeout(this.#timer);
  }

  /** Shows slide `index` (wrapping round at either end). */
  go(index, instant = false) {
    const count = this.slides.length;
    const target = ((index % count) + count) % count;
    this.track.scrollTo({
      left: target * this.track.clientWidth,
      behavior: instant || reducedMotion.matches ? 'instant' : 'smooth',
    });
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const { scrollLeft, clientWidth } = this.track;
    const index = Math.min(this.slides.length - 1, Math.max(0, Math.round(scrollLeft / Math.max(clientWidth, 1))));
    const changed = index !== this.#index;
    this.#index = index;

    this.slides.forEach((slide, i) => {
      slide.inert = i !== index;
    });
    this.dots.forEach((dot, i) => {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    if (changed) this.#schedule();
  }

  // Autoplay: on unless reduced motion; reasons to wait are held in #holds until they clear.
  get #interval() {
    return Number(this.dataset.autoplay) || 0;
  }

  #setUpAutoplay(signal) {
    if (!this.#interval || reducedMotion.matches) return;

    if (this.pauseButton) {
      this.pauseButton.hidden = false;
      this.pauseButton.addEventListener(
        'click',
        () => {
          const paused = !this.#holds.has('user');
          this.#hold('user', paused);
          this.pauseButton.classList.toggle('is-paused', paused);
          this.pauseButton.setAttribute(
            'aria-label',
            paused ? this.pauseButton.dataset.labelPlay : this.pauseButton.dataset.labelPause
          );
        },
        { signal }
      );
    }

    this.addEventListener(
      'pointerenter',
      (event) => event.pointerType === 'mouse' && this.#hold('hover', true),
      { signal }
    );
    this.addEventListener('pointerleave', () => this.#hold('hover', false), { signal });
    this.addEventListener('focusin', () => this.#hold('focus', true), { signal });
    this.addEventListener(
      'focusout',
      (event) => {
        if (!this.contains(event.relatedTarget)) this.#hold('focus', false);
      },
      { signal }
    );
    document.addEventListener('visibilitychange', () => this.#hold('hidden', document.hidden), { signal });
    reducedMotion.addEventListener('change', () => this.#hold('motion', reducedMotion.matches), { signal });

    this.#observer = new IntersectionObserver(([entry]) => this.#hold('away', !entry.isIntersecting));
    this.#observer.observe(this);
  }

  #hold(reason, on) {
    if (on) this.#holds.add(reason);
    else this.#holds.delete(reason);
    this.#schedule();
  }

  #schedule() {
    clearTimeout(this.#timer);
    if (!this.#interval || reducedMotion.matches || this.#holds.size > 0) return;
    this.#timer = setTimeout(() => this.go(this.#index + 1), this.#interval);
  }
}

class HeroVideo extends HTMLElement {
  #observer;

  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video || reducedMotion.matches || navigator.connection?.saveData) return;

    this.#observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        this.video
          .play()
          .then(() => this.classList.add('is-playing'))
          .catch(() => {
            // Autoplay refused (power saving, browser policy): the image stays.
          });
      } else {
        this.video.pause();
      }
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

if (!customElements.get('hero-slider')) customElements.define('hero-slider', HeroSlider);
if (!customElements.get('hero-video')) customElements.define('hero-video', HeroVideo);
